import { Drawer, Modal } from '@heroui/react';
import classNames from 'classnames';
import { useState } from 'react';

import usePlatform from '@/hooks/use-platform';
import useScrimThemeColor from '@/hooks/use-scrim-theme-color';
import MatrixLoader from '@/shared/components/MatrixLoader';

type ModalProps = {
  readonly className?: string;
  readonly title?: string;
  readonly href?: string;
  readonly onClose: () => void;
};

const IframeView = ({ title, href }: { readonly title: string; readonly href: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative size-full overflow-hidden rounded-2xl bg-surface-secondary">
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <MatrixLoader className="text-accent" />
        </div>
      )}
      <iframe
        title={title}
        src={href}
        sandbox="allow-same-origin"
        className={classNames(
          'size-full border-none bg-background opacity-0 transition-opacity duration-(--duration-fast) ease-(--ease-smooth-out) motion-reduce:transition-none',
          isLoaded && 'opacity-100'
        )}
        onLoad={() => {
          setIsLoaded(true);
        }}
        onError={() => {
          setIsLoaded(true);
        }}
      />
    </div>
  );
};

const IframeModal = ({ className, title = '', href = '', onClose }: ModalProps) => {
  const { isMobile } = usePlatform();
  const isOpen = Boolean(href);
  useScrimThemeColor(isOpen);

  // Keep the last document on screen while the overlay animates out.
  const [shown, setShown] = useState({ title, href });

  if (href && (href !== shown.href || title !== shown.title)) {
    setShown({ title, href });
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const view = shown.href ? (
    <IframeView key={shown.href} title={shown.title} href={shown.href} />
  ) : null;

  if (isMobile) {
    return (
      <Drawer.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
        <Drawer.Content placement="bottom">
          <Drawer.Dialog id="iframe-modal" className={classNames('h-[90dvh]', className)}>
            <Drawer.Handle />
            <Drawer.CloseTrigger />
            {/* Keeps a long title clear of the absolutely placed close button. */}
            <Drawer.Header className="pe-8">
              <Drawer.Heading>{shown.title}</Drawer.Heading>
            </Drawer.Header>
            <Drawer.Body>{view}</Drawer.Body>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    );
  }

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Container placement="center" size="lg">
        <Modal.Dialog id="iframe-modal" className={classNames('h-[80dvh]', className)}>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{shown.title}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>{view}</Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};

export default IframeModal;
